import {Alert, Button, Card, Collapse, Input, Space, Table, Tag, Typography} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {t} from "i18next";

// Rapor hazırlama kurallarının (hl7_client.rules) eklenebilir blokları. Backend ile AYNI biçim
// (RIS.Service.HL7.Cloud.ReportRules, uygulama: HL7ReportPrepareService) — yeni bir tip
// eklenirse iki taraf birlikte güncellenmeli.
//
// fields: kartta açılan alan tablosu — [alan, zorunlu mu, açıklama anahtarı].
export const REPORT_RULE_BLOCKS = [
    {
        type: 'SegmentOrder',
        title: 'Report Rule SegmentOrder',
        description: 'Report Rule SegmentOrder Description',
        example: {type: 'SegmentOrder', order: [4, 2, 3, 1]},
        fields: [
            ['type', true, 'REPORT_RULE_FIELD_TYPE'],
            ['order', true, 'REPORT_RULE_FIELD_ORDER']
        ]
    },
    {
        type: 'CopyValue',
        title: 'Report Rule CopyValue',
        description: 'Report Rule CopyValue Description',
        example: {
            type: 'CopyValue',
            source: {segment: 'PID', field: 3, type: ''},
            target: {segment: 'OBR', field: 18},
            prefix: '',
            suffix: '',
            overwrite: false
        },
        fields: [
            ['type', true, 'REPORT_RULE_FIELD_TYPE'],
            ['source.segment', 'REPORT_RULE_REQUIRED_IF_NO_SOURCE_TYPE', 'REPORT_RULE_FIELD_SOURCE_SEGMENT'],
            ['source.field', 'REPORT_RULE_REQUIRED_IF_NO_SOURCE_TYPE', 'REPORT_RULE_FIELD_SOURCE_FIELD'],
            ['source.type', false, 'REPORT_RULE_FIELD_SOURCE_TYPE'],
            ['target.segment', true, 'REPORT_RULE_FIELD_TARGET_SEGMENT'],
            ['target.field', true, 'REPORT_RULE_FIELD_TARGET_FIELD'],
            ['prefix', false, 'REPORT_RULE_FIELD_PREFIX'],
            ['suffix', false, 'REPORT_RULE_FIELD_SUFFIX'],
            ['overwrite', false, 'REPORT_RULE_FIELD_COPY_OVERWRITE']
        ]
    },
    {
        type: 'Static',
        title: 'Report Rule Static',
        description: 'Report Rule Static Description',
        example: {type: 'Static', value: 'RADYOLOJI', target: {segment: 'ORC', field: 21}, overwrite: true},
        fields: [
            ['type', true, 'REPORT_RULE_FIELD_TYPE'],
            ['value', true, 'REPORT_RULE_FIELD_VALUE'],
            ['target.segment', true, 'REPORT_RULE_FIELD_TARGET_SEGMENT'],
            ['target.field', true, 'REPORT_RULE_FIELD_TARGET_FIELD'],
            ['overwrite', true, 'REPORT_RULE_FIELD_STATIC_OVERWRITE']
        ]
    }
];

const KNOWN_TYPES = REPORT_RULE_BLOCKS.map(x => x.type);

const SOURCE_TYPES = ['patient_hbys_id', 'accession_number'];

const SEGMENT_PATTERN = /^[A-Z][A-Z0-9]{2}$/;

// HL7ReportPrepareService.GetReportContent ile aynı numaralar.
const PART_NAMES = {1: 'Technique', 2: 'Comparison', 3: 'Findings', 4: 'Conclusion and Recommendations'};

const isPermutationOf1To4 = (order) =>
    Array.isArray(order)
    && order.length === 4
    && order.every(Number.isInteger)
    && [...order].sort().join(',') === '1,2,3,4';

// Backend ile AYNI kural (ReportRules.ValidateField): OBR-18 → {segment: "OBR", field: 18}.
const validateField = (obj, path) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj))
        return t('REPORT_RULE_PATH_REQUIRED', {path});

    if (typeof obj.segment !== 'string' || !SEGMENT_PATTERN.test(obj.segment))
        return t('REPORT_RULE_SEGMENT_INVALID', {path});

    if (!Number.isInteger(obj.field) || obj.field < 1)
        return t('REPORT_RULE_FIELD_INVALID', {path});

    return null;
};

const validateSource = (source) => {
    if (!source || typeof source !== 'object' || Array.isArray(source))
        return t('REPORT_RULE_PATH_REQUIRED', {path: 'source'});

    if (!source.type)
        return validateField(source, 'source');

    return SOURCE_TYPES.includes(source.type)
        ? null
        : t('REPORT_RULE_SOURCE_TYPE_INVALID', {value: source.type, options: SOURCE_TYPES.join(', ')});
};

const validateRule = (rule) => {
    switch (rule.type) {
        case 'SegmentOrder':
            return isPermutationOf1To4(rule.order) ? null : t('Report rule order invalid');
        case 'CopyValue':
            return validateSource(rule.source) || validateField(rule.target, 'target');
        case 'Static':
            return rule.value === undefined || rule.value === null || rule.value === ''
                ? t('REPORT_RULE_VALUE_REQUIRED')
                : validateField(rule.target, 'target');
        default:
            return null;
    }
};

// Backend ile AYNI kural (ReportRules.Validate). Hata mesajı döner, geçerliyse null.
export const validateReportRulesText = (text) => {
    if (!text || !text.trim())
        return null;

    let rules;
    try {
        rules = JSON.parse(text);
    } catch (e) {
        return `${t('Report rules invalid JSON')}: ${e.message}`;
    }

    if (!Array.isArray(rules))
        return t('Report rules must be an array');

    for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];
        const prefix = `${t('Rule')} ${i + 1}`;

        if (!rule || typeof rule !== 'object' || Array.isArray(rule))
            return `${prefix}: ${t('Report rule must be an object')}`;

        if (!KNOWN_TYPES.includes(rule.type))
            return `${prefix}: ${t('Unknown report rule type')} '${rule.type}' (${KNOWN_TYPES.join(', ')})`;

        const error = validateRule(rule);
        if (error)
            return `${prefix} (${rule.type}): ${error}`;
    }

    if (rules.filter(x => x.type === 'SegmentOrder').length > 1)
        return t('Report rule SegmentOrder once');

    return null;
};

// Kayıttan gelen değeri okunur hâle getirir; JSON değilse olduğu gibi bırakır (kullanıcı düzeltir).
export const formatReportRules = (text) => {
    if (!text || !text.trim())
        return '[]';

    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    } catch (e) {
        return text;
    }
};

const fieldRef = (obj) => `${obj.segment}-${obj.field}`;

const describeSource = (source) => {
    if (source.type === 'patient_hbys_id')
        return t('REPORT_RULE_SOURCE_PATIENT_HBYS_ID');
    if (source.type === 'accession_number')
        return t('REPORT_RULE_SOURCE_ACCESSION_NUMBER');
    return fieldRef(source);
};

// Tek bir kuralın düz Türkçe karşılığı. HL7ReportPrepareService'in yaptığını anlatır;
// warning: kural kaydedilebilir ama beklenen işi yapmaz.
const describeRule = (rule) => {
    const error = validateRule(rule);
    if (error)
        return {text: error, error: true};

    switch (rule.type) {
        case 'SegmentOrder':
            return {
                text: t('REPORT_RULE_SUMMARY_ORDER', {
                    parts: rule.order.map(x => `${x} ${t(PART_NAMES[x])}`).join(' → ')
                })
            };
        case 'CopyValue': {
            const added = [
                rule.prefix && t('REPORT_RULE_SUMMARY_PREFIX', {prefix: rule.prefix}),
                rule.suffix && t('REPORT_RULE_SUMMARY_SUFFIX', {suffix: rule.suffix})
            ].filter(Boolean);
            const wrapped = added.length ? ' ' + t('REPORT_RULE_SUMMARY_WRAPPED', {parts: added.join(', ')}) : '';
            return {
                text: t('REPORT_RULE_SUMMARY_COPY', {source: describeSource(rule.source), target: fieldRef(rule.target)})
                    + wrapped + ' '
                    + t(rule.overwrite ? 'REPORT_RULE_SUMMARY_OVERWRITE' : 'REPORT_RULE_SUMMARY_KEEP')
            };
        }
        case 'Static':
            return rule.overwrite
                ? {text: t('REPORT_RULE_SUMMARY_STATIC', {value: rule.value, target: fieldRef(rule.target)})}
                : {text: t('REPORT_RULE_SUMMARY_STATIC_SKIPPED', {target: fieldRef(rule.target)}), warning: true};
        default:
            return {text: rule.type, error: true};
    }
};

function RulesSummary({text}) {
    let rules;
    try {
        rules = text.trim() ? JSON.parse(text) : [];
    } catch (e) {
        return <Alert type={'error'} showIcon message={`${t('Report rules invalid JSON')}: ${e.message}`}/>;
    }

    if (!Array.isArray(rules))
        return <Alert type={'error'} showIcon message={t('Report rules must be an array')}/>;

    if (rules.length === 0)
        return <Alert type={'info'} showIcon message={t('REPORT_RULE_SUMMARY_EMPTY')}/>;

    const hasOrder = rules.some(x => x?.type === 'SegmentOrder');

    return (
        <Card size={'small'} title={t('REPORT_RULE_SUMMARY_TITLE')}>
            <ol style={{margin: 0, paddingLeft: 20, fontSize: 12}}>
                {rules.map((rule, i) => {
                    const known = rule && typeof rule === 'object' && KNOWN_TYPES.includes(rule.type);
                    const line = known
                        ? describeRule(rule)
                        : {text: `${t('Unknown report rule type')} '${rule?.type}'`, error: true};

                    return (
                        <li key={i} style={{marginBottom: 4}}>
                            {line.error && <Tag color={'red'}>{t('Error')}</Tag>}
                            {line.warning && <Tag color={'orange'}>{t('REPORT_RULE_NOT_APPLIED')}</Tag>}
                            <Typography.Text type={line.error ? 'danger' : undefined}>{line.text}</Typography.Text>
                        </li>
                    );
                })}
            </ol>
            {!hasOrder && (
                <Typography.Text type={'secondary'} style={{fontSize: 12}}>
                    {t('REPORT_RULE_SUMMARY_DEFAULT_ORDER')}
                </Typography.Text>
            )}
        </Card>
    );
}

function BlockFields({fields}) {
    return (
        <Table
            size={'small'}
            pagination={false}
            rowKey={x => x[0]}
            dataSource={fields}
            columns={[
                {title: t('Field'), dataIndex: 0, width: 130, render: v => <Typography.Text code>{v}</Typography.Text>},
                {
                    title: t('Required'),
                    dataIndex: 1,
                    width: 110,
                    render: v => v === true
                        ? <Tag color={'red'}>{t('Yes')}</Tag>
                        : v === false ? <Tag>{t('No')}</Tag> : <Tag color={'orange'}>{t(v)}</Tag>
                },
                {title: t('Description'), dataIndex: 2, render: v => <span style={{fontSize: 12}}>{t(v)}</span>}
            ]}
        />
    );
}

// antd <Form.Item> içinde kullanılır (value / onChange). Üstte nasıl çalıştığı, ortada JSON ve
// canlı özet, altta eklenebilir bloklar: "Ekle" bloğun örneğini diziye ekler, kullanıcı
// değerleri JSON'da düzenler; her bloğun alan tablosu kartın içinde açılır.
export default function ReportRulesEditor({value, onChange}) {
    const text = value ?? '';

    // JSON okunamıyorsa ekleme kapalı; kural hatası varken (örn. eksik alan) blok eklenebilir.
    let parsed = null;
    try {
        const rules = text.trim() ? JSON.parse(text) : [];
        parsed = Array.isArray(rules) ? rules : null;
    } catch (e) {
        parsed = null;
    }

    // SegmentOrder tek olabilir — varsa yenisi eklenmez, mevcut olan düzenlenir.
    const canAdd = (block) =>
        parsed !== null && !(block.type === 'SegmentOrder' && parsed.some(x => x?.type === 'SegmentOrder'));

    const addBlock = (block) => {
        if (canAdd(block))
            onChange && onChange(JSON.stringify([...parsed, block.example], null, 2));
    };

    return (
        <Space direction={'vertical'} style={{width: '100%'}} size={8}>
            <Alert type={'info'} showIcon style={{fontSize: 12}} message={t('REPORT_RULE_HOW_IT_WORKS')}/>

            <Input.TextArea
                value={text}
                onChange={e => onChange && onChange(e.target.value)}
                autoSize={{minRows: 4, maxRows: 18}}
                spellCheck={false}
                style={{fontFamily: 'monospace', fontSize: 12}}
                placeholder={'[]'}
            />

            <RulesSummary text={text}/>

            <Typography.Text type={'secondary'} style={{fontSize: 12}}>
                {t('Report rules available blocks')}
            </Typography.Text>

            {REPORT_RULE_BLOCKS.map(block => (
                <Card
                    key={block.type}
                    size={'small'}
                    title={<>{t(block.title)} <Typography.Text code>{block.type}</Typography.Text></>}
                    extra={
                        <Button
                            size={'small'}
                            icon={<PlusOutlined/>}
                            disabled={!canAdd(block)}
                            title={canAdd(block) ? undefined : t(parsed === null ? 'REPORT_RULE_FIX_JSON_FIRST' : 'Report rule SegmentOrder once')}
                            onClick={() => addBlock(block)}
                        >
                            {t('Add')}
                        </Button>
                    }
                >
                    <Typography.Paragraph style={{fontSize: 12, marginBottom: 6}}>
                        {t(block.description)}
                    </Typography.Paragraph>
                    <pre style={{fontSize: 11, margin: '0 0 6px', whiteSpace: 'pre-wrap'}}>
                        {JSON.stringify(block.example, null, 2)}
                    </pre>
                    <Collapse
                        size={'small'}
                        items={[{
                            key: 'fields',
                            label: t('REPORT_RULE_FIELDS_TITLE'),
                            children: <BlockFields fields={block.fields}/>
                        }]}
                    />
                </Card>
            ))}
        </Space>
    );
}
